<script setup lang="ts">
useHead({
  title: 'Privacy Policy — Arvee Events',
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: 'description',
      content:
        'Privacy Policy for Arvee Events — how we collect, use, and protect your information.',
    },
  ],
});

const config = useRuntimeConfig();

const portalPrivacyTopUrl = computed(() =>
  useCrossPropertyLink({
    url: `${config.public.portalUrl}/privacy-policy`,
    destination: 'portal',
    campaign: 'legal_link',
    placementId: 'privacy_page_top',
  }),
);

const portalPrivacyCcpaUrl = computed(() =>
  useCrossPropertyLink({
    url: `${config.public.portalUrl}/privacy-policy`,
    destination: 'portal',
    campaign: 'legal_link',
    placementId: 'privacy_ccpa_inline',
  }),
);
</script>

<template>
  <div>
    <h1 class="text-h4 mb-2">Privacy Policy</h1>
    <p class="text-body-2 text-medium-emphasis mb-6">
      Last updated: August 2026
    </p>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">1. Introduction</h2>
      <p class="text-body-1 mb-3">
        This policy describes how Arvee Solutions, Inc. ("Arvee," "we," "us," or
        "our") collects, uses, and protects information through Arvee Events
        (events.arvee.io).
      </p>
      <p class="text-body-1 mb-3">
        This supplements the general
        <a :href="portalPrivacyTopUrl" target="_blank" rel="noopener"
          >Arvee Privacy Policy</a
        >, which governs all Arvee services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">2. Information We Collect</h2>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Account Information</h3>
      <p class="text-body-1 mb-3">
        Shared with the main Arvee platform via cross-subdomain authentication.
        This includes your name, email, and profile photo.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Event Information</h3>
      <p class="text-body-1 mb-3">
        Event details, descriptions, images, and expense breakdowns uploaded by
        organizers.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Payment Information</h3>
      <p class="text-body-1 mb-3">
        Processed entirely by Stripe. Arvee does not store raw card data. We
        retain transaction records (transaction IDs, amounts, dates,
        commitment/purchase status) for compliance and dispute resolution.
        Organizers who onboard with Stripe Connect share additional account
        information with Stripe.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Usage Data</h3>
      <p class="text-body-1 mb-3">
        Pages visited, interactions with events (views, commits, purchases),
        device and browser information. We use self-hosted analytics in
        cookieless mode to collect this data without persistent identifiers on
        your device.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Communications</h3>
      <p class="text-body-1 mb-3">
        Messages and interactions on the platform may be logged for moderation
        and safety.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Error Reporting</h3>
      <p class="text-body-1 mb-3">
        Exception data via Sentry with PII collection disabled.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">3. How We Use Your Information</h2>
      <p class="text-body-1 mb-3">We use your information to:</p>
      <ul class="text-body-1 mb-3 ml-6">
        <li>Operate and maintain Arvee Events</li>
        <li>Process event payments and manage threshold-based funding</li>
        <li>
          Communicate about your events, commitments, and purchases
          (transactional emails)
        </li>
        <li>
          Share relevant attendee information with event organizers for
          coordination
        </li>
        <li>Enforce our Terms of Service and prevent fraud</li>
        <li>Improve the platform</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">4. Information Sharing</h2>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Stripe</h3>
      <p class="text-body-1 mb-3">
        Payment processing (card data, Connect account data for organizers). See
        <a href="https://stripe.com/privacy" target="_blank" rel="noopener"
          >Stripe's privacy policy</a
        >.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Event Organizers</h3>
      <p class="text-body-1 mb-3">
        When you commit to or purchase a ticket for an event, the organizer
        receives your name and basic profile information for event coordination
        purposes.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Other Participants</h3>
      <p class="text-body-1 mb-3">
        Commitment and attendance information is reflected in aggregate event
        statistics (e.g., "X committed") but individual participant details are
        not publicly displayed.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Meta (Advertising)</h3>
      <p class="text-body-1 mb-3">
        On the Arvee Events site (events.arvee.io) we use the Meta pixel,
        provided by Meta Platforms, Inc. (which operates Facebook and
        Instagram). It collects page views and browsing activity, your
        interactions with events (views, commits, purchases), and technical
        identifiers such as your IP address, browser and device information, and
        Meta cookies. This information is sent to Meta, a third party, and is
        used to measure advertising performance, show you Arvee ads on Meta
        platforms and elsewhere (retargeting), and reach similar audiences
        (lookalike audiences). You can opt out of this advertising sharing — see
        <NuxtLink to="/do-not-sell">Your Privacy Choices</NuxtLink>.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Legal Requirements</h3>
      <p class="text-body-1 mb-3">
        We may disclose information if required by law, subpoena, or court
        order, or to protect the rights, safety, or property of Arvee or others.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">
        Advertising, Sales, and Sharing
      </h3>
      <p class="text-body-1 mb-3">
        We do <strong>not</strong> sell your personal information for money.
        However, our use of the Meta pixel to build advertising audiences and
        measure conversions involves sharing personal information — such as your
        activity and conversion events — with Meta for cross-context behavioral
        advertising. Under some U.S. state privacy laws, including the CCPA as
        amended by the CPRA, this may be considered a "sale" or "share" of
        personal information. You can opt out of this sharing at
        <NuxtLink to="/do-not-sell">Your Privacy Choices</NuxtLink>; we honor
        the Global Privacy Control (GPC) browser signal, and you can also email
        <a href="mailto:support@arvee.io">support@arvee.io</a>.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">5. Cookies and Authentication</h2>
      <p class="text-body-1 mb-3">
        Arvee Events uses bearer-token single sign-on for authentication. Your
        access token is kept in your browser's local storage — not in a cookie —
        and sent with each API request. Sign-in is shared across Arvee services
        through Arvee SSO, so you only need to log in once.
      </p>
      <p class="text-body-1 mb-3">
        The site does set one small cookie to remember your "Do Not Sell or
        Share" opt-out choice if you make one at
        <NuxtLink to="/do-not-sell">Your Privacy Choices</NuxtLink>, so we can
        honor that preference on future visits.
      </p>
      <p class="text-body-1 mb-3">
        Our self-hosted analytics platform, which runs on our own infrastructure
        to help us understand how visitors use the site, operates in cookieless
        mode and does not set any cookies or store persistent identifiers on
        your device.
      </p>
      <p class="text-body-1 mb-3">
        Separately, the Meta advertising pixel used on this site does set
        cookies and send data to Meta, a third party, unless you opt out. You
        can opt out of this advertising sharing at
        <NuxtLink to="/do-not-sell">Your Privacy Choices</NuxtLink>.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">6. Data Retention</h2>
      <ul class="text-body-1 mb-3 ml-6">
        <li>
          <strong>Account data:</strong> retained while your account is active
          across Arvee services.
        </li>
        <li>
          <strong>Payment records:</strong> retained for compliance and dispute
          resolution per legal requirements.
        </li>
        <li>
          <strong>Event data:</strong> retained for the lifetime of the event
          and a reasonable period after completion.
        </li>
        <li>
          <strong>Moderation/communication logs:</strong> retained as needed for
          safety and compliance.
        </li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">7. Data Security</h2>
      <ul class="text-body-1 mb-3 ml-6">
        <li>All data transmitted over HTTPS/TLS</li>
        <li>Payment data handled by Stripe (PCI-compliant)</li>
        <li>Server-side access controls on all API endpoints</li>
        <li>Error tracking via Sentry with PII disabled</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">8. Your Rights</h2>
      <p class="text-body-1 mb-3">
        You may request access to your personal data. You may request deletion
        of your account (subject to retention requirements for payment and
        compliance records).
      </p>
      <p class="text-body-1 mb-3">
        You may opt out of the sale or sharing of your personal information,
        including cross-context behavioral advertising via the Meta pixel. Use
        <NuxtLink to="/do-not-sell">Your Privacy Choices</NuxtLink> to do so; we
        also honor the Global Privacy Control (GPC) browser signal.
      </p>
      <p class="text-body-1 mb-3">
        California residents (CCPA/CPRA), Canadian residents (PIPEDA), and
        Mexican residents (LFPDPPP) have additional rights as described in the
        general
        <a :href="portalPrivacyCcpaUrl" target="_blank" rel="noopener"
          >Arvee Privacy Policy</a
        >.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">9. Children's Privacy</h2>
      <p class="text-body-1 mb-3">
        Arvee Events is intended for users 13 years of age and older. We do not
        knowingly collect personal information from children under 13.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">10. Changes to This Policy</h2>
      <p class="text-body-1 mb-3">
        We may update this policy at any time. Continued use after changes
        constitutes acceptance.
      </p>
    </section>

    <section>
      <h2 class="text-h5 mb-3">11. Contact</h2>
      <p class="text-body-1 mb-3">
        Questions about this policy? Email us at
        <a href="mailto:support@arvee.io">support@arvee.io</a>.
      </p>
      <p class="text-body-1">
        Arvee Solutions, Inc.<br />
        2803 Philadelphia Pike Suite B-1290<br />
        Claymont, DE 19703
      </p>
    </section>
  </div>
</template>
